import * as Sentry from '@sentry/react';
import type React from 'react';
import { useEffect } from 'react';
import { APP_ENV } from '../../lib/constants.ts';
import { useAppState } from '../../lib/hooks/use-app-state.ts';
import { UserType } from '../../lib/types.ts';
import { getFullName } from '../../utils/name.ts';

type Props = {
    children: React.ReactNode | React.ReactNode[];
};

export const SentryProvider: React.FC<Props> = ({ children }) => {
    const { platoUser, superAdmin, primaryUserType, tenantId } = useAppState();

    useEffect(() => {
        if (typeof window === 'undefined') {
            return;
        }
        if (process.env.NODE_ENV === 'development') {
            return;
        }

        Sentry.setTag('environment', APP_ENV ?? 'unknown');

        if (primaryUserType === UserType.SuperAdmin && superAdmin) {
            Sentry.setUser({
                IS_SUPER_ADMIN: true,
                tenantId,
                id: superAdmin.id,
                name: getFullName(superAdmin.firstName, superAdmin.lastName),
                firstName: superAdmin.firstName,
                lastName: superAdmin.lastName,
                email: superAdmin.email,
                role: 'SUPER_ADMIN',
            });
        } else if (primaryUserType === UserType.PlatoUser && platoUser) {
            Sentry.setUser({
                IS_SUPER_ADMIN: false,
                tenantId,
                id: platoUser.id,
                name: getFullName(platoUser.firstName, platoUser.lastName),
                firstName: platoUser.firstName,
                lastName: platoUser.lastName,
                email: platoUser.email,
                role: platoUser.role,
            });
        }
        Sentry.setTags({
            admin: !!superAdmin,
            impersonation: superAdmin !== undefined && platoUser !== undefined,
            tenantId,
            tenantSlug: platoUser?.platoCustomer.slug,
            userRole: superAdmin ? 'SUPER_ADMIN' : platoUser?.role,
        });
    }, [platoUser, superAdmin, primaryUserType, tenantId]);

    return children;
};
